import { Sparkles, HeartHandshake } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Companion } from '../data/companions'
import type { Mood } from '@/lib/mood'

const MOOD_COLOR: Record<Mood, string | null> = {
  neutral: null,
  concerned: 'var(--info-text)',
  celebratory: 'var(--warning-text)',
}

// A living coach avatar. Renders a real portrait when one is set, otherwise a warm gradient +
// initial. Always "breathes"; when `speaking`, shows a voice equalizer synced to the audio we
// play. `mood` reacts to what's actually being discussed — a soft concerned glow while working
// through a problem, a warm celebratory one on a win — so the coach visibly responds instead of
// wearing one fixed expression all conversation. A photo can't change its face, so this is
// carried by color/motion around it rather than pretending the portrait itself is smiling.
export function CompanionAvatar({
  c, size = 44, speaking = false, mood = 'neutral',
}: {
  c: Companion; size?: number; speaking?: boolean; mood?: Mood
}) {
  const eqH = Math.max(6, Math.round(size * 0.22))
  const moodColor = MOOD_COLOR[mood]
  const showBadge = mood !== 'neutral' && size >= 32
  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center rounded-full shadow-sm avatar-breathe overflow-visible"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <span
        className={cn('relative block h-full w-full overflow-hidden rounded-full', moodColor && 'avatar-mood-ring')}
        style={moodColor ? { boxShadow: `0 0 0 2px ${moodColor}`, ['--mood-color' as string]: moodColor } : undefined}
      >
        {c.photo ? (
          <img src={c.photo} alt="" className="h-full w-full rounded-full object-cover" draggable={false} />
        ) : (
          <span
            className="flex h-full w-full items-center justify-center rounded-full font-semibold text-white"
            style={{ fontSize: size * 0.42, backgroundImage: `linear-gradient(135deg, ${c.gradient[0]}, ${c.gradient[1]})` }}
          >
            {c.name[0]}
          </span>
        )}

        {/* Speaking equalizer — reads as "talking", tinted to the coach. */}
        {speaking && (
          <span
            className="absolute inset-x-0 bottom-0 flex items-end justify-center gap-[2px] rounded-b-full bg-black/25 pb-[8%] pt-[6%] backdrop-blur-[1px]"
            style={{ color: '#fff' }}
          >
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="eq-bar w-[2px] rounded-full bg-current"
                style={{ height: eqH, animationDelay: `${i * 120}ms` }}
              />
            ))}
          </span>
        )}
      </span>

      {/* Mood badge — small, only at sizes where it reads clearly. */}
      {showBadge && (
        <span
          className="absolute -right-0.5 -top-0.5 flex items-center justify-center rounded-full text-white ring-2 ring-[var(--card)] animate-scale-in"
          style={{ width: size * 0.36, height: size * 0.36, minWidth: 14, minHeight: 14, backgroundColor: moodColor! }}
        >
          {mood === 'celebratory'
            ? <Sparkles style={{ width: '60%', height: '60%' }} />
            : <HeartHandshake style={{ width: '60%', height: '60%' }} />}
        </span>
      )}
    </span>
  )
}
